import { select, all, call, fork, put, takeLatest, takeEvery, delay, getContext } from 'redux-saga/effects';

import { getUploadUrl, uploadFile, getAgents } from '../../api/api';
import * as actions from './actions';
import {
  deleteFile,
  deleteRecordedInterviewFile,
  deleteInterviewFile,
  deleteFinanceFile,
  deleteFollowUpFile,
  deleteDocumentFile,
} from '../student/actions';
import { logout } from '../auth/actions';
import { DataActionTypes } from './types';
import { getStudent, getUI, getUniId } from '../selectors';
import { StudentState } from '../student/types';
import { UIState } from '../ui/types';
import { deleteFile as deleteFileUiAction } from '../ui/actions';

function* handleGetUploadUrl(action: ReturnType<typeof actions.getUploadUrl>): any {
  try {
    const student: StudentState = yield select(getStudent);
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleGetUploadUrl');
      return;
    }
    const url = yield call(getUploadUrl, idToken, student.core.university, student.core.student_id.split('#')[1], {
      openModal: action.payload.openModal,
      fileName: action.payload.fileName,
      fileType: action.payload.fileType,
    });
    yield put(actions.uploadFile(action.payload.file, url));
  } catch (e: any) {
    yield put(actions.getUploadUrlFailure(e.message));
  }
}

function* handleUploadFile(action: ReturnType<typeof actions.uploadFile>) {
  try {
    const { file, url } = action.payload;

    yield call(uploadFile, file, url);

    const ui: UIState = yield select(getUI);
    const {
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      interviewType,
      eventType,
      eventKey,
      attemptHash,
      interviewHash,
      fileNames,
      isFinanceDocumentModal,
      isFollowUpDocumentModal,
      isInterviewDocumentModal,
      isRecordedInterviewDocumentModal,
    } = ui.openModal || {};
    yield put(
      actions.uploadFileSuccess(
        sponsorType ?? null,
        sponsorKey ?? null,
        docType ?? null,
        docKey ?? null,
        interviewType ?? '',
        eventType ?? '',
        eventKey ?? '',
        interviewHash ?? '',
        attemptHash ?? '',
        fileNames ?? [],
        isFinanceDocumentModal || false,
        isFollowUpDocumentModal || false,
        isInterviewDocumentModal || false,
        isRecordedInterviewDocumentModal || false,
      ),
    );
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.uploadFileFailure(err.message));
    } else {
      yield put(actions.uploadFileFailure('An unknown error occured.'));
    }
  }
}

function* handleDeleteFile(action: ReturnType<typeof actions.deleteFile>) {
  try {
    yield put(deleteFileUiAction(action.payload.fileName));
    const ui: UIState = yield select(getUI);
    const {
      sponsorType,
      sponsorKey,
      docType,
      docKey,
      interviewType,
      eventType,
      eventKey,
      interviewHash,
      attemptHash,
      fileNames,
      isFinanceDocumentModal,
      isFollowUpDocumentModal,
      isInterviewDocumentModal,
      isRecordedInterviewDocumentModal,
    } = ui.openModal || {};

    yield put(
      deleteFile(
        sponsorType ?? null,
        sponsorKey ?? null,
        docType ?? null,
        docKey ?? null,
        interviewType ?? '',
        eventType ?? '',
        eventKey ?? '',
        interviewHash ?? '',
        attemptHash ?? '',
        fileNames ?? [],
        !(
          isFinanceDocumentModal ||
          isFollowUpDocumentModal ||
          isInterviewDocumentModal ||
          isRecordedInterviewDocumentModal
        ),
        isFinanceDocumentModal || false,
        isFollowUpDocumentModal || false,
        isInterviewDocumentModal || false,
        isRecordedInterviewDocumentModal || false,
      ),
    );

    if (action.payload.isRecordedInterview) {
      yield put(
        deleteRecordedInterviewFile(
          action.payload.interviewType,
          action.payload.interviewHash,
          action.payload.attemptHash,
          action.payload.fileName,
          action.payload.deletedBy ?? '',
        ),
      );
    } else if (action.payload.isInterview) {
      yield put(
        deleteInterviewFile(
          action.payload.interviewType,
          action.payload.eventType,
          action.payload.eventKey,
          action.payload.fileName,
          action.payload.deletedBy ?? '',
        ),
      );
    } else if (action.payload.isFinance) {
      yield put(
        deleteFinanceFile(
          action.payload.sponsorType,
          action.payload.sponsorKey,
          action.payload.docType,
          action.payload.docKey,
          action.payload.fileName,
          action.payload.deletedBy ?? '',
        ),
      );
    } else if (action.payload.isFollowUp) {
      yield put(
        deleteFollowUpFile(
          action.payload.sponsorType,
          action.payload.sponsorKey,
          action.payload.docType ?? '',
          action.payload.docKey ?? '',
          action.payload.fileName,
          action.payload.deletedBy ?? '',
        ),
      );
    } else {
      yield put(
        deleteDocumentFile(
          action.payload.sponsorType,
          action.payload.sponsorKey!,
          action.payload.docType,
          action.payload.docKey,
          action.payload.fileName,
          action.payload.deletedBy ?? '',
        ),
      );
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.deleteFileFailure(err.message));
    } else {
      yield put(actions.deleteFileFailure('An unknown error occurred.'));
    }
  }
}

function* handleGetAgents(action: ReturnType<typeof actions.getAgents>) {
  try {
    const context = yield getContext('sagaContext');
    const idToken: any = yield call(context.getIdToken);
    const uni = yield select(getUniId);
    const searchQuery = action.payload;
    if (!idToken) {
      yield put(logout());
      yield call(context.onLogout);
      console.log('Refresh Token has Expired - handleGetAgents');
      return;
    }
    if (!searchQuery) {
      return yield put(actions.getAgentsSuccess([]));
    }
    yield delay(200);
    const agents = yield call(getAgents, idToken, uni, searchQuery);
    yield put(actions.getAgentsSuccess(agents));
  } catch (err) {
    if (err instanceof Error) {
      yield put(actions.getAgentsFailure(err.message));
    } else {
      yield put(actions.getAgentsFailure('An unknown error occurred.'));
    }
  }
}

function* watchDeleteFile() {
  yield takeEvery(DataActionTypes.DELETE_FILE, handleDeleteFile);
}

function* watchFileUpload() {
  yield takeEvery(DataActionTypes.GET_UPLOAD_FILE_URL, handleGetUploadUrl);
  yield takeEvery(DataActionTypes.UPLOAD_FILE, handleUploadFile);
}

function* watchGetAgents() {
  // NB! using "takeLatest" as this event gets fired frequently
  // this is the debounce pattern in sagas
  yield takeLatest(DataActionTypes.GET_AGENTS, handleGetAgents);
}

function* DataSaga() {
  yield all([fork(watchGetAgents)]);
  yield all([fork(watchFileUpload)]);
  yield all([fork(watchDeleteFile)]);
}

export default DataSaga;
